import Vue from "vue";
import vSelect from "vue-select";
import { mapGetters } from "vuex"
import { sortBy } from "lodash";
import { TmsService, OemManualsService } from "@/services/";
import { IComponentGroupsRequestDto } from "@/types/ComponentGroupsRequestDto";
import { LoadingSpinner } from "@/components";
import { DnRouterLink } from "@/components/Elements";
import PartSearchFacets from "@/components/SubMenus/PartSearchFacets.vue";
import { faChevronDown, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import ComponentTreeNode from "./ComponentTreeNode.vue";
import { TourService } from "@/services/TourService";

/**
 * PartSearchTruckSelector – Multi-step "wizard" for selecting a truck make,
 * model, and component to search parts for. Supports two flows:
 *
 *  1. **OEM / OemManuals flow** – Make -> Model -> Component
 *     Group -> Component (produces a diagram search).
 *  2. **Competitive / TMS flow** – Make -> Model -> Category (produces a
 *     traditional parts list search).
 *
 * Can run as a standalone page or in `embedded` mode inside PartDiagramPage
 */
export default Vue.extend({
    name: "PartSearchTruckSelector",
    props: {
        /** Pre-selected make (for deep-linking). */
        make: {
            type: String,
            default: null
        },
        /** Pre-selected model (for deep-linking). */
        model: {
            type: String,
            default: null
        },
        /** Pre-selected category (for deep-linking). */
        category: {
            type: String,
            default: null
        },
        /** Pre-selected component group (for deep-linking). */
        componentGroup: {
            type: String,
            default: null
        },
        /** Pre-selected component (for deep-linking). */
        component: {
            type: String,
            default: null
        },
        /** When true, renders as a compact sidebar panel inside PartDiagramPage. */
        embedded: {
            type: Boolean,
            default: false
        },
        /** Available facets from search results (used in embedded mode). */
        availableFacets: {
            type: Object,
            default: () => ({})
        },
        /** Currently selected facets (used in embedded mode). */
        externalSelectedFacets: {
            type: Object,
            default: () => ({})
        }
    },
    components: {
        LoadingSpinner,
        DnRouterLink,
        vSelect,
        PartSearchFacets,
        FontAwesomeIcon,
        ComponentTreeNode
    },
    data: () => ({
        // --- Loading flags ---
        loading: true,
        loadingModels: false,
        loadingCategories: false,
        loadingComponents: false,
        loadingComponentGroups: false,

        // --- Serial Number ---
        serialNumber: "",
        serialNumberLoading: false,
        serialNumberError: "",
        serialMatchedMakes: null as any[] | null,
        serialMatchedPublications: {} as Record<number, any[]>,

        // --- Selections ---
        selectedMake: "" as any,
        selectedModel: "" as any,
        selectedComponent: null as any,
        selectedComponentGroup: "" as any,
        selectedCategory: "",

        // --- Option lists ---
        makes: [] as any[],
        models: [] as any[],
        componentGroups: [] as any[],
        displayedComponents: [] as any[],

        /** IDs of parent components whose children are currently expanded. */
        expandedComponentIds: [] as string[],
        categories: [] as any[],

        // --- Flags ---
        showDiagram: false,
        IsOemMake: false,
        isRestoring: false,
        modelAutoSelected: false,
        componentGroupAutoSelected: false,

        // --- Facet-related (used only in non-embedded / standalone mode) ---
        selectedFacets: {} as Record<string, any>,
        searchTerm: "",
        isTms: true,

        // --- Icons ---
        chevronDownIcon: faChevronDown,
        clearIcon: faTimes,

        // --- Loading timeout tracking ---
        isLoadingTooLong: false,
        _loadingTimeoutId: null as any,
    }),
    computed: {
        ...mapGetters(["user", "audienceIds", "activeAccountId", "McfCostCenterId"]),

        /** Makes filtered by serial number results (if serial was searched). */
        filteredMakes(): any[] {
            if (this.serialMatchedMakes) {
                return this.serialMatchedMakes;
            }
            return this.makes;
        },

        /** Placeholder text for the Model dropdown. */
        ModelPlaceholder(): string {
            if (this.loadingModels) return "Loading models...";
            if (this.selectedMake && !this.loadingModels && !this.models.length) {
                return "No models found for this make.";
            }
            return this.selectedMake === "CLARK" ? "Type or Select a Lot" : "Type or Select a Model";
        },

        /** Placeholder text for the Component Group dropdown. */
        ComponentGroupPlaceholder(): string {
            if (this.loadingComponentGroups) return "Loading Publications...";
            if (this.selectedModel && !this.loadingComponentGroups && !this.componentGroups.length) {
                return "No Publications found for this model.";
            }
            return "Type or Select a Publication";
        },

        /** Placeholder text for the Category dropdown. */
        CategoryPlaceholder(): string {
            if (this.loadingCategories) return "Loading categories...";
            if (this.selectedMake && this.selectedModel && !this.loadingCategories && !this.categories.length) {
                return "No categories found for this model.";
            }
            return "Type or Select a Category";
        }
    },
    watch: {
        /** React to make changes: determine flow (OEM vs competitive) and load next step. */
        selectedMake(newMake: any) {
            // Skip watcher side-effects during state restoration from query params
            if (this.isRestoring) return;

            this.IsOemMake = !!(this.IsNotNullOrUndefined(newMake?.BrandID) && newMake.BrandID !== 0);
            this.showDiagram = this.IsOemMake;
            if (this.IsOemMake) {
                this.GetModelsOemManuals();
            } else if (newMake) {
                this.GetCompetitiveModels();
            }

            this.updateQueryString();

            this.$emit("make-selected", {
                IsOemMake: this.IsOemMake,
                BrandID: newMake ? newMake.BrandID : null,
                showDiagram: this.showDiagram
            });
        },

        /** Auto-clear the serial search state if the user backspaces the field to empty. */
        serialNumber(newVal: string, oldVal: string) {
            if (newVal === "" && oldVal && (this.serialMatchedMakes !== null || this.serialNumberError || this.serialNumberLoading)) {
                this.ResetFromMake();
            }
        }
    },
    created() {
        // If make and model props are provided, use GetData to restore state.
        // Skip this when a guided tour is active so the tour steps interact with
        // a clean selector instead of one pre-filled/auto-selected from props.
        if (this.make && this.model && !TourService.tourActive) {
            this.GetData();
        } else {
            this.GetMakes();
        }

        // Listen for browser back/forward to reset selections when
        // query params are cleared. Query params are managed via
        // history.replaceState so $route does not update on popstate.
        if (this.embedded) {
            this._onPopState = () => {
                this.selectedMake = "";
                this.clearDependentSelections("model");
                this.showDiagram = false;
                this.IsOemMake = false;
            };
            window.addEventListener("popstate", this._onPopState);
        }
    },
    beforeDestroy() {
        if (this._onPopState) {
            window.removeEventListener("popstate", this._onPopState);
        }
    },
    methods: {
        // ─── Event forwarding ──────────────────────────────────────────

        /** Forward facet-update event to the parent. */
        UpdateFacets() {
            this.$emit("updateFacets");
        },

        /** Forward truck-selection redirect event to the parent. */
        redirectToTruckSelect() {
            this.$emit("truckSelectionRedirect");
        },

        // ─── Serial Number Search ──────────────────────────────────────

        /** Handles serial number entry — searches for matching makes/models via GetPublications. */
        async onSerialNumberEntered() {
            const serial = (this.serialNumber || "").trim();
            if (!serial) return;

            this.serialNumberError = "";
            this.serialNumberLoading = true;
            this.serialMatchedMakes = null;
            this.serialMatchedPublications = {};
            this.selectedMake = "";
            this.clearDependentSelections("model");
            this.showDiagram = false;
            this.IsOemMake = false;

            try {
                // Searching by serial number alone does not require a BrandId — the
                // backend matches across all brands. Only RegionId, AccountId
                // (passed separately below) and SerialNumber are needed.
                const componentGroupsRequest = {
                    RegionId: parseInt(this.McfCostCenterId) + 290,
                    BrandId: null,
                    ModelId: null,
                    TruckTypeId: null,
                    SerialNumber: serial
                };

                const response = await OemManualsService.GetComponentGroups(
                    this.user.UserId,
                    componentGroupsRequest,
                    this.activeAccountId
                );

                if (!response?.ComponentGroups?.length) {
                    this.serialNumberError = "No OEM results found. Please try browsing using the Select Make option.";
                    return;
                }

                // Group the flat list of component groups by the BrandId returned
                // on each item, then map those BrandIds back to their make objects.
                const matchedMakes: any[] = [];
                const matchedPubs: Record<number, any[]> = {};

                for (const group of response.ComponentGroups as any[]) {
                    const brandId = group.BrandId;
                    if (!brandId) continue;

                    if (!matchedPubs[brandId]) {
                        matchedPubs[brandId] = [];
                        const make = this.makes.find((m: any) => m.BrandID === brandId);
                        if (make) matchedMakes.push(make);
                    }
                    matchedPubs[brandId].push(group);
                }

                if (matchedMakes.length === 0) {
                    this.serialNumberError = "No OEM results found. Please try browsing using the Select Make option.";
                    return;
                }

                // Store matched data
                this.serialMatchedMakes = sortBy(matchedMakes, (m: any) => (m.OemDescription || "").toLowerCase());
                this.serialMatchedPublications = matchedPubs;

                if (matchedMakes.length === 1) {
                    // Single match — auto-select the make
                    this.selectedMake = matchedMakes[0];
                } else {
                    // Multiple matches — open the make dropdown so the user can pick from the filtered list
                    this.focusSelect('makeSelect');
                }
            } catch (error) {
                this.serialNumberError = "No OEM results found. Please try browsing using the Select Make option.";
            } finally {
                this.serialNumberLoading = false;
            }
        },

        /** Clears serial number search state. */
        clearSerialSearch() {
            this.serialNumber = "";
            this.serialNumberError = "";
            this.serialMatchedMakes = null;
            this.serialMatchedPublications = {};
        },

        // ─── Data fetching ─────────────────────────────────────────────

        /** Fetches the list of available makes (OEMs) from TmsService. */
        async GetMakes() {
            try {
                this.loading = true;
                const searchInput = this.CreateSearchInput();
                const response = await TmsService.GetOems(searchInput);
                this.makes = response?.data ?? [];

                // Try to restore the selectedMake from the prop, unless a guided
                // tour is active — in that case leave the selector untouched.
                if (this.make && this.makes.length > 0 && !TourService.tourActive) {
                    const matchedMake = this.makes.find((m: any) =>
                        m.OemDescription === this.make
                    );
                    if (matchedMake) {
                        this.selectedMake = matchedMake;
                    }
                }
            } catch (error) {
                this.makes = [];
            } finally {
                this.loading = false;
            }
        },

        /**
         * Bootstraps the selector when make and model are pre-selected
         * (e.g. from query string deep-link). Uses isRestoring flag to
         * prevent the selectedMake watcher from firing side-effects.
         * Weird specific logic that the business requested instead of just changing the truck selection
         */
        async GetData() {
            
            this.isRestoring = true;
            try {
                this.loading = true;
                await this.GetMakes();

                if (this.selectedMake) {
                    // Determine if OEM or competitive based on make
                    this.IsOemMake = !!(this.IsNotNullOrUndefined(this.selectedMake.BrandID) && this.selectedMake.BrandID !== 0);
                    this.showDiagram = this.IsOemMake;
                    // Emit make-selected so parent knows the flow type
                    this.$emit("make-selected", {
                        IsOemMake: this.IsOemMake,
                        BrandID: this.selectedMake.BrandID || null,
                        showDiagram: this.showDiagram
                    });

                    // Load models
                    if (this.IsOemMake) {
                        await this.GetModelsOemManuals();
                    } else {
                        await this.GetCompetitiveModels();
                    }

                    // Try to restore the model from the prop
                    if (this.model && this.models.length > 0) {
                        // Competitive models are plain strings (or { Model }); OEM models are objects.
                        const matchedModel = this.models.find((m: any) =>
                            (m === this.model) ||
                            (m?.Model === this.model) ||
                            (m?.ModelName === this.model) ||
                            (m?.displayLabel === this.model)
                        );
                        if (matchedModel) {
                            this.selectedModel = matchedModel;
                        }
                    }

                    // If we have a model selected, continue restoration
                    if (this.selectedModel) {
                        if (this.IsOemMake) {
                            // OEM flow: restore component group and component
                            await this.GetComponentGroups();
                            if (this.componentGroup && this.componentGroups.length > 0) {
                                const matchedGroup = this.componentGroups.find((cg: any) =>
                                    cg.PublicationModuleTitle === this.componentGroup
                                );
                                if (matchedGroup) {
                                    this.selectedComponentGroup = matchedGroup;
                                    await this.GetComponentsOemManuals();

                                    // Try to find and select the component
                                    if (this.component && this.displayedComponents.length > 0) {
                                        const findComponent = (components: any[], ancestors: string[]): { comp: any, path: string[] } | null => {
                                            for (const comp of components) {
                                                if (comp.componentDescription === this.component || comp.name === this.component) {
                                                    return { comp, path: ancestors };
                                                }
                                                if (comp.children && comp.children.length > 0) {
                                                    const found = findComponent(comp.children, [...ancestors, comp.id]);
                                                    if (found) return found;
                                                }
                                            }
                                            return null;
                                        };

                                        const match = findComponent(this.displayedComponents, []);
                                        if (match) {
                                            this.expandedComponentIds = match.path;
                                            this.selectedComponent = match.comp;

                                            // Re-trigger the search so the parent loads results (also loads the diagram)
                                            await this.GoToOemManualsResults(match.comp);
                                        }
                                    }
                                }
                            }
                        } else {
                            // Competitive flow: restore category
                            await this.GetCategories();

                            if (this.category && this.categories.length > 0) {
                                const matchedCategory = this.categories.find((cat: string) => cat === this.category);
                                if (matchedCategory) {
                                    this.selectedCategory = matchedCategory;
                                    // Re-trigger the search so the parent loads results
                                    this.OnCategorySelected();
                                }
                            }
                        }
                    }
                }
            } catch (error) {
                console.error("Error in GetData:", error);
            } finally {
                this.loading = false;
                this.isRestoring = false;
            }
        },

        /** Fetches models for a competitive (non-OEM) make via TmsService. */
        async GetCompetitiveModels() {
            try {
                this.models = ["loading models..."];
                this.loadingModels = true;
                this.modelAutoSelected = false;
                this.clearDependentSelections("model");
                this.focusSelect('modelSelectCompetitive');

                const facets = { OemDescription: this.selectedMake.OemDescription };
                const searchInput = this.CreateSearchInput(facets);
                const response = await TmsService.GetModels(searchInput);
                this.models = response?.data ?? [];

                if (this.models.length === 1 && !this.isRestoring) {
                    this.modelAutoSelected = true;
                    this.selectedModel = this.models[0];
                    await this.GetCategories();
                }
            } catch (error) {
                console.error("Error loading models:", error);
                this.models = [];
            } finally {
                this.loadingModels = false;
                this.focusSelect('modelSelectCompetitive');
            }
        },

        /** Fetches models for an OEM make via OemManualsService. */
        async GetModelsOemManuals() {
            try {
                this.loadingModels = true;
                this.modelAutoSelected = false;
                this.clearDependentSelections("model");

                if (!this.selectedMake?.BrandID) return;

                // If this make was resolved via a serial-number search, we already have
                // every matching model (and its component groups) from that search —
                // no need to hit the Models endpoint again. Skip focusSelect here since
                // it would prematurely reopen the dropdown after auto-selection below
                // (this path resolves synchronously, unlike the network call further down).
                const serialPubs = this.serialMatchedPublications[this.selectedMake.BrandID];
                if (this.serialNumber?.trim() && serialPubs?.length) {
                    this.PopulateModelsFromSerialResults(serialPubs);
                    return;
                }

                this.focusSelect('modelSelect');

                const response = await OemManualsService.GetModels(
                    this.user.UserId,
                    this.selectedMake.BrandID,
                    this.McfCostCenterId,
                    null //no truck class
                );

                if (response.error) throw new Error(response.error.Message);
                const products = (response.Products || []).map((m: any) => {
                    return { ...m, displayLabel: m.ModelName };
                });

                this.models = products;

                if (this.models.length === 1 && !this.isRestoring) {
                    this.modelAutoSelected = true;
                    this.selectedModel = this.models[0];
                    await this.GetComponentGroups();
                }
            } catch (error) {
                console.error("Error loading models:", error);
                this.models = [];
            } finally {
                this.loadingModels = false;
                // Don't focus if model was auto-selected (component groups are already loading/loaded)
                if (!this.modelAutoSelected) {
                    this.focusSelect('modelSelect');
                }
            }
        },

        /**
         * Builds the Model dropdown directly from the component groups already
         * returned by the serial-number search, avoiding a redundant Models call.
         */
        PopulateModelsFromSerialResults(serialPubs: any[]) {
            const modelMap = new Map<number, any>();
            for (const pub of serialPubs) {
                if (pub.ModelId != null && !modelMap.has(pub.ModelId)) {
                    modelMap.set(pub.ModelId, { ModelId: pub.ModelId, ModelName: pub.ModelName, displayLabel: pub.ModelName });
                }
            }
            this.models = sortBy(Array.from(modelMap.values()), (m: any) => (m.ModelName || "").toLowerCase());

            if (this.models.length === 1 && !this.isRestoring) {
                this.modelAutoSelected = true;
                this.selectedModel = this.models[0];
                this.PopulateComponentGroupsFromSerialResults();
            }
        },

        /** Fetches component groups for the selected OEM model via OemManualsService. */
        async GetComponentGroups() {
            try {
                this.loadingComponentGroups = true;
                this.componentGroupAutoSelected = false;
                this.clearDependentSelections("componentGroup");
                if (!this.selectedMake?.BrandID) return;

                // If this make/model was resolved via a serial-number search, the
                // component groups were already returned by that search — no need
                // to hit the ComponentGroups endpoint again. Skip focusSelect here
                // since it would prematurely reopen the dropdown after auto-selection
                // below (this path resolves synchronously, unlike the network call
                // further down).
                const serialPubs = this.serialMatchedPublications[this.selectedMake.BrandID];
                if (this.serialNumber?.trim() && serialPubs?.length) {
                    await this.PopulateComponentGroupsFromSerialResults();
                    return;
                }

                this.focusSelect('componentGroupSelect');

                // Once a specific model is resolved, the serial number has already
                // done its job of narrowing make/model — sending it alongside a
                // concrete ModelId causes the downstream manuals API to 500.
                const componentGroupsRequest = {
                    RegionId: parseInt(this.McfCostCenterId) + 290,
                    BrandId: this.selectedMake.BrandID,
                    ModelId: this.selectedModel.ModelId || null,
                    TruckTypeId: this.selectedModel.TruckTypeId || null,
                    SerialNumber: null
                };

                const response = await OemManualsService.GetComponentGroups(
                    this.user.UserId,
                    componentGroupsRequest,
                    this.activeAccountId
                );

                if (response.error) throw new Error(response.error.Message);

                // Alphabetize publication names
                this.componentGroups = sortBy(
                    response.ComponentGroups || [],
                    (cg: any) => (cg.PublicationModuleTitle || "").toLowerCase()
                );

                if (this.componentGroups.length === 1 && !this.isRestoring) {
                    this.componentGroupAutoSelected = true;
                    this.selectedComponentGroup = this.componentGroups[0];
                    await this.GetComponentsOemManuals();
                    this.updateQueryString();
                }
            } catch (error) {
                console.error("Error loading component groups:", error);
                this.componentGroups = [];
            } finally {
                this.loadingComponentGroups = false;
                // Don't focus if component group was auto-selected (components are already loaded)
                if (!this.componentGroupAutoSelected) {
                    this.focusSelect('componentGroupSelect');
                }
            }
        },

        /**
         * Builds the Component Group (Publication) dropdown directly from the
         * component groups already returned by the serial-number search,
         * filtered to the currently selected model — avoids a redundant
         * ComponentGroups call.
         */
        async PopulateComponentGroupsFromSerialResults() {
            // Reset here since this method is sometimes called directly (bypassing
            // GetComponentGroups) when a model is auto-selected from serial results.
            this.componentGroupAutoSelected = false;

            const serialPubs = this.serialMatchedPublications[this.selectedMake.BrandID] || [];
            const matching = serialPubs.filter((pub: any) => pub.ModelId === this.selectedModel.ModelId);

            this.componentGroups = sortBy(
                matching,
                (cg: any) => (cg.PublicationModuleTitle || "").toLowerCase()
            );

            if (this.componentGroups.length === 1 && !this.isRestoring) {
                this.componentGroupAutoSelected = true;
                this.selectedComponentGroup = this.componentGroups[0];
                await this.GetComponentsOemManuals();
                this.updateQueryString();
            } else {
                // Multiple matches — open the publication dropdown so the user can pick from the list
                this.focusSelect('componentGroupSelect');
            }
        },

        /** Fetches leaf components within a component group via OemManualsService. */
        async GetComponentsOemManuals() {
            try {
                this.$emit("selection-reset");
                this.loadingComponents = true;
                this.isLoadingTooLong = false;
                this.selectedComponent = null;
                this.displayedComponents = [];

                // Set a timeout to show a message if loading takes more than 3 seconds
                this._loadingTimeoutId = setTimeout(() => {
                    this.isLoadingTooLong = true;
                }, 3000);
                // Use != null (not truthy) checks — PublicationModuleId/BomId are
                // legitimate integer IDs that can be 0, which is falsy in JS.
                if (this.selectedMake?.BrandID == null
                    || this.selectedComponentGroup?.PublicationModuleId == null
                    || this.selectedComponentGroup?.BomId == null) return;

                const response = await OemManualsService.GetComponents(
                    this.selectedComponentGroup?.PublicationModuleId, this.selectedComponentGroup?.DataSource, this.selectedComponentGroup.BomId
                );

                // Response is a tree array with Path, DisplayName, PartDescription, Components[].
                // Leaf nodes have PartId & PartNumber; branch nodes have non-empty Components.
                // Some levels return a flat mix of sub-category headers (no PartId, no
                // own Components) interleaved with parts (PartId > 0). These must be
                // regrouped so that headers contain their associated parts.
                const responseArray = Array.isArray(response) ? response : (response.Components || []);

                const isPart = (node: any): boolean => node.PartId != null && node.PartId > 0;

                let nodeCounter = 0;
                const mapNode = (comp: any): any | null => {
                    if (isPart(comp)) return null;

                    const children = comp.Components || [];
                    const leafParts = children.filter(isPart).map((p: any) => ({
                        PartId: p.PartId,
                        PartNumber: p.PartNumber,
                        PartDescription: p.PartDescription,
                        FullHierarchyPath: p.FullHierarchyPath || p.Path,
                        EffectiveFrom: p.EffectiveFrom,
                        EffectiveTo: p.EffectiveTo,
                        // Matches the `name` attribute on the diagram SVG's hotspot <g hsid="..."> elements
                        ItemNumber: p.ItemNumber,
                        AssemblyHeaderNode: p.AssemblyHeaderNode,
                        NonSubAssemblyArtId: p.NonSubAssemblyArtId,
                        SubAssemblyArtId: p.SubAssemblyArtId,
                    }));
                    const branchChildren = children
                        .map(mapNode)
                        .filter((c: any) => c !== null);

                    const name = comp.PartDescription || comp.DisplayName || 'Unknown';

                    return {
                        id: `node-${nodeCounter++}`,
                        name,
                        componentDescription: name,
                        hasChildren: branchChildren.length > 0,
                        children: branchChildren,
                        parts: leafParts,
                        FullHierarchyPath: comp.FullHierarchyPath || comp.Path,
                    };
                };

                this.displayedComponents = responseArray.map(mapNode).filter((c: any) => c !== null);
            } catch (error) {
                console.error("Error loading components:", error);
                this.displayedComponents = [];
            } finally {
                this.loadingComponents = false;
                this.isLoadingTooLong = false;
                // Clear the loading timeout if it hasn't fired yet
                if (this._loadingTimeoutId !== null) {
                    clearTimeout(this._loadingTimeoutId);
                    this._loadingTimeoutId = null;
                }
                // Blur the component group select so the dropdown closes after selection
                this.$nextTick(() => {
                    const vSelectRef = this.$refs['componentGroupSelect'] as any;
                    if (vSelectRef && vSelectRef.searchEl) {
                        vSelectRef.searchEl.blur();
                    }
                });
            }
        },

        /** Fetches categories for the selected competitive make + model. */
        async GetCategories() {
            try {
                this.loadingCategories = true;
                this.selectedCategory = "";
                this.categories = ["loading categories..."];
                this.focusSelect('categorySelect');
                const facets = {
                    OemDescription: this.selectedMake.OemDescription,
                    Model: this.selectedModel
                };
                const searchInput = this.CreateSearchInput(facets);
                this.categories = (await TmsService.GetCategories(searchInput)).data;
            } catch (error) {
                console.error("Error in GetCategories:", error);
                this.categories = [];
            } finally {
                this.loadingCategories = false;
                this.focusSelect('categorySelect');
            }
        },

        // ─── Selection handlers ────────────────────────────────────────

        /** Handles model selection – loads component groups (OEM) or categories (competitive). */
        async OnModelSelected() {
            if (!this.selectedModel) return;
            if (this.isRestoring) return;
            if (this.modelAutoSelected) {
                this.modelAutoSelected = false;
                return;
            }
            try {
                this.$emit("selection-reset");
                if (this.IsOemMake && this.showDiagram) {
                    await this.GetComponentGroups();
                } else {
                    await this.GetCategories();
                }
                this.updateQueryString();
            } catch (error) {
                console.error("Error in OnModelSelected:", error);
            }
        },

        /** Handles category selection for competitive makes. */
        async OnCategorySelected() {
            if (!this.selectedCategory) return;
            try {
                this.updateQueryString();
                const queryData = {
                    OemDescription: this.selectedMake.OemDescription,
                    Model: this.selectedModel,
                    Category: this.selectedCategory,
                    showDiagram: false
                };
                if (this.embedded) {
                    this.$emit("search-complete", queryData);
                } else {
                    this.$router.push({ name: "TruckModelSearch", query: queryData });
                }
            } catch (error) {
                console.error("Error in OnCategorySelected:", error);
            }
        },

        /** Handles component group selection – loads components and updates query. */
        async OnComponentGroupSelected() {
            if (!this.selectedComponentGroup) return;
            if (this.isRestoring) return;
            if (this.componentGroupAutoSelected) {
                this.componentGroupAutoSelected = false;
                return;
            }
            await this.GetComponentsOemManuals();
            this.updateQueryString();
        },

        /** Selects a leaf component and navigates to results. */
        SelectComponent(component: any) {
            this.selectedComponent = component;
            if (!component.hasChildren) {
                if (this.showDiagram) {
                    this.GoToOemManualsResults(component);
                } else {
                    this.GoToResultsPage();
                }
            }
        },

        /** Toggles a component's expanded/collapsed state; selects leaf nodes. */
        ToggleComponent(component: any) {
            if (component.hasChildren) {
                // Toggle expanded state for parent components
                const idx = this.expandedComponentIds.indexOf(component.id);
                if (idx >= 0) {
                    this.expandedComponentIds.splice(idx, 1);
                } else {
                    this.expandedComponentIds.push(component.id);
                }
            } else {
                //changes will go here!!!
                // Leaf component: select and navigate
                this.selectedComponent = component;
                this.$nextTick(() => window.scrollTo({ top: 0, behavior: 'smooth' }));
                if (this.showDiagram) {
                    this.GoToOemManualsResults(component);
                } else {
                    this.GoToResultsPage();
                }
            }
        },

        // ─── Navigation ────────────────────────────────────────────────

        /** Navigates to OemManuals diagram results for the given component. */
        async GoToOemManualsResults(component: any) {
            try {
                this.updateQueryString();
                let art = component.parts.filter(p => p.ItemNumber == null);
                // Collect all leaf parts from this node and its descendants
                const collectParts = (node: any): any[] => {
                    let parts = [...(node.parts || [])];
                    if (node.children) {
                        for (const child of node.children) {
                            parts = parts.concat(collectParts(child));
                        }
                    }
                    return parts;
                };
                const parts = collectParts(component);

                const group = this.selectedComponentGroup;
                const componentGroupId = group?.PublicationModuleId != null && component?.componentDescription
                    ? `${group.PublicationModuleId}|${component.componentDescription}`
                    : null;

                const apArtId = parts.map(p => p.NonSubAssemblyArtId).find(id => id != null);
                const queryData = {
                    userId: this.user.UserId,
                    BrandID: this.selectedMake.BrandID,
                    componentGroupId,
                    oemDescription: this.selectedMake.OemDescription,
                    model: this.selectedModel.ModelName,
                    componentGroup: this.selectedComponentGroup.PublicationModuleTitle,
                    component: component.name,
                    showDiagram: true,
                    inlineParts: parts,
                    diagramSvgData: null,
                    extArtId: art.length > 0 ? art[0].PartId : null,
                    apArtId: apArtId ?? null,   
                };

                if (this.embedded) {
                    this.$emit("search-complete", queryData);
                } else {
                    this.$router.push({ name: "DiagramSearch", query: queryData as any });
                }
            } catch (error) {
                console.error("Error in GoToOemManualsResults:", error);
            }
        },

        /** Navigates to the traditional results page for a selected component. */
        GoToResultsPage() {
            if (!this.selectedComponent) return;
            const queryData = {
                OemDescription: this.selectedMake.OemDescription,
                Model: this.selectedModel,
                Component: this.selectedComponent.id,
                ComponentName: this.selectedComponent.componentDescription || this.selectedComponent.name
            };
            if (this.embedded) {
                this.$emit("search-complete", queryData);
            } else {
                this.$router.push({ name: "DiagramSearch", query: queryData });
            }
        },

        // ─── Reset helpers ─────────────────────────────────────────────

        /**
         * Clears dependent selections and option arrays from a given level
         * downward. Use this when an upstream selection changes to cascade-reset
         * all downstream choices.
         *
         * @param fromLevel - The level to start clearing from:
         *   "model" | "componentGroup" | "component" | "category"
         */
        clearDependentSelections(fromLevel: string) {
            const levels = ["model", "componentGroup", "component", "category"];
            const startIdx = levels.indexOf(fromLevel);
            if (startIdx < 0) return;

            const clearFrom = levels.slice(startIdx);

            if (clearFrom.includes("model")) {
                this.selectedModel = "";
                this.models = ["loading models..."];
            }
            if (clearFrom.includes("componentGroup")) {
                this.selectedComponentGroup = "";
                this.componentGroups = [];
            }
            if (clearFrom.includes("component")) {
                this.selectedComponent = null;
                this.displayedComponents = [];
                this.expandedComponentIds = [];
            }
            if (clearFrom.includes("category")) {
                this.selectedCategory = "";
                this.categories = [];
            }
        },

        /**
         * Scrolls a given element into view on the next tick.
         * Used by breadcrumb reset actions.
         */
        scrollToElement(selector: string) {
            this.$nextTick(() => {
                const el = document.querySelector(selector);
                if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            });
        },

        /** Reset everything back to the make selector. */
        ResetFromMake() {
            this.selectedMake = "";
            this.clearDependentSelections("model");
            this.clearSerialSearch();
            this.showDiagram = false;
            this.updateQueryString();
            this.scrollToElement("#makeSelect");
        },

        /** Reset from model selection downward — keeps models list intact. */
        ResetFromModel() {
            this.selectedModel = null;
            this.selectedComponentGroup = null;
            this.componentGroups = [];
            this.selectedComponent = null;
            this.displayedComponents = [];
            this.expandedComponentIds = [];
            this.selectedCategory = null;
            this.categories = [];
            this.$emit("selection-reset");
            this.updateQueryString();
            this.scrollToElement("#modelSelect");
        },

        /** Reset category selection. */
        ResetFromCategory() {
            this.selectedCategory = null;
            this.$emit("selection-reset");
            this.updateQueryString();
            this.scrollToElement("#categorySelect");
        },

        /** Reset from component group selection downward — keeps componentGroups list intact. */
        ResetFromComponentGroup() {
            this.selectedComponentGroup = null;
            this.selectedComponent = null;
            this.displayedComponents = [];
            this.expandedComponentIds = [];
            this.$emit("selection-reset");
            this.updateQueryString();
            this.scrollToElement("#componentGroupSelect");
        },

        /** Reset component selection only. */
        ResetFromComponent() {
            this.selectedComponent = null;
            this.expandedComponentIds = [];
            this.$emit("selection-reset");
            this.updateQueryString();
            this.scrollToElement(".component-list");
        },

        // ─── Utilities ─────────────────────────────────────────────────

        /**
         * Updates the browser's query string to reflect the current selections.
         * Uses history.replaceState instead of $router.replace to avoid
         * triggering a component re-create from <router-view :key="$route.fullPath">.
         */
        updateQueryString() {
            if (!this.embedded) return; // Only update query string in embedded mode

            const query: Record<string, string> = {};

            // Update or remove make
            if (this.selectedMake) {
                query.OemDescription = this.selectedMake.OemDescription || this.selectedMake;
            }

            // Update or remove model
            if (this.selectedModel) {
                query.Model = this.selectedModel.ModelName || this.selectedModel;
            }

            // Update or remove category (competitive flow)
            if (this.selectedCategory) {
                query.Category = this.selectedCategory;
            }

            // Update or remove component group (OEM flow)
            if (this.selectedComponentGroup) {
                query.ComponentGroup = this.selectedComponentGroup.PublicationModuleTitle || this.selectedComponentGroup;
            }

            // Update or remove component (OEM flow)
            if (this.selectedComponent) {
                query.Component = this.selectedComponent.componentDescription || this.selectedComponent.name || this.selectedComponent;
            }

            // Build the new URL with query params
            const params = new URLSearchParams(query);
            const newUrl = `${window.location.pathname}?${params.toString()}`;

            // Only update if changed — uses history API directly to avoid
            // Vue Router's reactivity and the router-view :key re-render
            if (newUrl !== `${window.location.pathname}${window.location.search}`) {
                history.replaceState(history.state, '', newUrl);
            }
        },

        /** Builds a TMS search-input object, optionally including facets. */
        CreateSearchInput(facets?: object) {
            const searchInput: Record<string, any> = {
                AccountId: this.activeAccountId,
                UserId: this.user.UserId,
                DealerCode: this.user.DealerCode,
                Audiences: this.audienceIds
            };
            if (facets) {
                searchInput.Facets = facets;
            }
            return searchInput;
        },

        /**
         * Handles Tab keydown on a v-select — makes Tab behave exactly like
         * Enter, but only when the dropdown is open with options available.
         * If the dropdown is closed (already selected) or has no options
         * (still loading), we let the browser's default Tab behavior through
         * so focus moves forward naturally.
         */
        handleTab(event: KeyboardEvent, refName: string) {
            const vSelectRef = this.$refs[refName] as any;
            if (vSelectRef && vSelectRef.open && vSelectRef.typeAheadPointer >= 0
                && vSelectRef.filteredOptions && vSelectRef.filteredOptions.length > 0) {
                event.preventDefault();
                vSelectRef.typeAheadSelect();
            }
            // Otherwise: don't preventDefault — browser Tab moves focus normally
        },

        /**
         * vue-select resets its highlight to the first option whenever options change;
         * clear it when the dropdown opens so options only highlight on hover or arrow keys.
         */
        clearHighlight(refName: string) {
            this.$nextTick(() => {
                const vSelectRef = this.$refs[refName] as any;
                if (vSelectRef && !vSelectRef.search) {
                    vSelectRef.typeAheadPointer = -1;
                }
            });
        },

        /**
         * Programmatically focuses a v-select by ref name after the DOM has
         * settled.  Skips focus during state restoration (GetData) and only
         * focuses when the target v-select is enabled and actually has
         * options to display (not still loading with an empty list).
         */
        focusSelect(refName: string) {
            // Don't steal focus during deep-link restoration
            if (this.isRestoring) return;

            // Don't steal focus when a guided tour is active
            if (TourService.tourActive) return;

            // Use a short delay so the v-select has time to render
            // with its new options after the reactive data update.
            this.$nextTick(() => {
                this.$nextTick(() => {
                    const vSelectRef = this.$refs[refName] as any;
                    if (vSelectRef
                        && !vSelectRef.disabled
                        && vSelectRef.filteredOptions
                        && vSelectRef.filteredOptions.length > 0) {
                        const searchInput = vSelectRef.$refs?.search
                            || vSelectRef.$el?.querySelector('input[type="search"]');
                        if (searchInput) {
                            searchInput.focus();
                        }
                    }
                });
            });
        },
    }
});